import { useEffect } from 'react'
import { Link, Outlet, useNavigate, useRouter } from '@tanstack/react-router'

import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth'
import { buttonVariants } from '@/ui/button-variants'
import { focusMain, useRouteFocus, MAIN_CONTENT_ID } from '@/lib/use-route-focus'
import { useSelectedEntityId } from '@/lib/use-selected-entity'
import { ImpersonationBanner } from '@/app/shell/impersonation-banner'
import { TopBar } from '@/app/shell/top-bar'
import { AppProvider, ASSISTANT_INSET_CLASS, useAppContext } from '@/app/shell/app-context'
import { useDocumentTitle } from '@/app/shell/use-document-title'
import { useEntityContextSync } from '@/app/shell/use-entity-context'
import { AnnouncerProvider } from '@/shared/providers/announcer-provider'
import { AssistantBrowserCommandProvider, useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'
import { AccessHierarchyBrowserEvents } from '@/modules/iam'
import { EntityBrowserEvents, useEntityBrandSync, useRecordPageVisit } from '@/modules/hierarchy'
import { useNotificationHub } from '@/modules/notifications'
import { PendingAccessNotice } from '@/modules/invitations'
import { AssistantPanel } from '@/modules/assistant'
import { assistantPath } from '@/app/routes/assistant-navigation'

function SkipLink() {
  return (
    <a
      href={`#${MAIN_CONTENT_ID}`}
      className="sr-only focus:not-sr-only focus:bg-background focus:text-foreground focus:ring-ring focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-md focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:shadow focus:outline-none focus-visible:ring-2"
    >
      Skip to main content
    </a>
  )
}

function EntityUnavailable() {
  useEffect(() => {
    if (document.activeElement === document.body) focusMain()
  }, [])
  return (
    <div className="flex flex-col items-start gap-3 p-6">
      <h1 className="text-foreground text-lg font-semibold">Entity unavailable</h1>
      <p role="alert" className="text-muted-foreground text-sm">
        This entity doesn't exist or you don't have access to it.
      </p>
      <Link
        to="/"
        activeOptions={{ exact: true }}
        className={buttonVariants({ variant: 'outline', size: 'sm' })}
      >
        Go to home
      </Link>
    </div>
  )
}

function Main({
  impersonating,
  entityUnavailable,
}: {
  impersonating: boolean
  entityUnavailable: boolean
}) {
  const navigate = useNavigate()
  const router = useRouter()
  useAssistantBrowserCommand(assistantBrowserCommands.focusMain, () => {
    document.getElementById(MAIN_CONTENT_ID)?.focus()
    return document.activeElement?.id === MAIN_CONTENT_ID
  })
  useAssistantBrowserCommand(assistantBrowserCommands.navigate, async (command) => {
    const path = assistantPath(command.page, command.entityId, command.targetId)
    if (!path) return false
    const previous = router.state.location.pathname
    try {
      await navigate({ to: path as '/' })
    } catch {
      return false
    }
    return previous !== path && router.state.location.pathname === path
  })
  return (
    <main
      id={MAIN_CONTENT_ID}
      tabIndex={-1}
      aria-label="Main content"
      className={cn(
        'bg-sidebar fixed inset-x-0 bottom-0 flex scroll-mt-12 flex-col overflow-hidden focus:outline-none',
        ASSISTANT_INSET_CLASS,
        impersonating ? 'top-18' : 'top-10',
      )}
    >
      <div className="bg-background empty:hidden mx-2 shrink-0 px-6 pt-6">
        <PendingAccessNotice />
      </div>
      <div className="bg-background mx-2 mb-2 min-h-0 flex-1 overflow-y-auto rounded-xl dark:border-[3px] dark:border-sidebar-border has-[[data-product-layout]]:contents">
        {entityUnavailable ? <EntityUnavailable /> : <Outlet />}
      </div>
    </main>
  )
}

function Assistant() {
  const {
    assistantOpen,
    setAssistantOpen,
    assistantWidth,
    setAssistantWidth,
    assistantToggleRef,
  } = useAppContext()
  return (
    <AssistantPanel
      open={assistantOpen}
      setOpen={setAssistantOpen}
      width={assistantWidth}
      setWidth={setAssistantWidth}
      toggleRef={assistantToggleRef}
    />
  )
}

export function AppShell() {
  const entityUnavailable = useEntityContextSync()
  useEntityBrandSync(useSelectedEntityId())
  useNotificationHub()
  useRouteFocus()
  useRecordPageVisit()
  useDocumentTitle(entityUnavailable ? 'Entity unavailable' : undefined)
  const { user } = useAuth()
  const impersonating = user?.impersonation != null
  return (
    <AssistantBrowserCommandProvider>
      <AppProvider>
        <AnnouncerProvider>
          <SkipLink />
          <AccessHierarchyBrowserEvents />
          <EntityBrowserEvents />
          <ImpersonationBanner />
          <TopBar impersonating={impersonating} />
          <Main impersonating={impersonating} entityUnavailable={entityUnavailable} />
          <Assistant />
        </AnnouncerProvider>
      </AppProvider>
    </AssistantBrowserCommandProvider>
  )
}
